import * as React from "react"
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md"
import { Box, IconButton, useBreakpointValue } from "@chakra-ui/react"
import useEmblaCarousel from "embla-carousel-react"

import { EventItemFragment } from "lib/graphql"

import { FeedEventTile } from "./FeedEventTile"

interface Props {
  events: EventItemFragment[]
  eventsCount: number
}

export function RecommendedEventsSlider({ events, eventsCount }: Props) {
  const [currentIndex, setCurrentIndex] = React.useState(0)
  const isMobile = useBreakpointValue({ base: true, md: false })
  const displayCount = isMobile ? 1 : 2

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    slidesToScroll: displayCount,
    duration: 30,
  })

  const onSelect = React.useCallback(() => {
    if (!emblaApi) return
    setCurrentIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  React.useEffect(() => {
    if (!emblaApi) return
    onSelect()
    emblaApi.on("select", onSelect)
    emblaApi.on("reInit", onSelect)

    return () => {
      emblaApi.off("select", onSelect)
      emblaApi.off("reInit", onSelect)
    }
  }, [emblaApi, onSelect])

  React.useEffect(() => {
    if (emblaApi) {
      emblaApi.reInit()
      setCurrentIndex(0)
    }
  }, [isMobile, emblaApi])

  const scrollPrev = React.useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev()
  }, [emblaApi])

  const scrollNext = React.useCallback(() => {
    if (emblaApi) emblaApi.scrollNext()
  }, [emblaApi])

  const canScrollPrev = currentIndex > 0
  const canScrollNext = currentIndex + displayCount < eventsCount

  return (
    <Box id="my-embla-carousel-recommended-events" position="relative">
      {eventsCount > displayCount && (
        <>
          <IconButton
            zIndex={500}
            position="absolute"
            top="85px"
            left={0}
            colorScheme="gray"
            variant="ghost"
            aria-label="vorige dia"
            icon={<MdKeyboardArrowLeft size={48} />}
            onClick={(e) => {
              e.stopPropagation()
              scrollPrev()
            }}
            disabled={!canScrollPrev}
            width="3rem"
          />
          <IconButton
            zIndex={500}
            position="absolute"
            top="85px"
            right={0}
            colorScheme="gray"
            variant="ghost"
            aria-label="volgende dia"
            icon={<MdKeyboardArrowRight size={48} />}
            onClick={(e) => {
              e.stopPropagation()
              scrollNext()
            }}
            disabled={!canScrollNext}
            width="3rem"
            ml={5}
          />
        </>
      )}
      <Box className="embla" ref={emblaRef} overflow="hidden">
        <Box className="embla__container" display="flex">
          {events.map((event, index) => (
            <Box
              key={`recommended:${event.id}`}
              cursor="pointer"
              className="embla__slide"
              flex={`0 0 calc(${100 / displayCount}% - ${displayCount > 1 ? (30 * (displayCount - 1)) / displayCount : 0}px)`}
              minW={0}
              mr={displayCount > 1 && index < events.length - 1 ? "30px" : 0}
            >
              <Box display="flex" w="100%">
                <FeedEventTile event={event} />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  )
}
